import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './TrendAnalysis.css';
import violationDescriptions from '../../utils/violationDescriptions';
import tooltipTexts from '../../utils/tooltipTexts';
import { API_BASE_URL } from '../../api/apiClient';

/** Map low values to green and high values to red (0–1). */
const getBarColorFromRatio = (ratio) => {
  const t = Math.max(0, Math.min(1, ratio));

  // green -> yellow -> red
  const stops = [
    { stop: 0, color: [67, 160, 71] },    // #43a047
    { stop: 0.5, color: [251, 192, 45] },  // #fbc02d
    { stop: 1, color: [229, 57, 53] }      // #e53935
  ];

  let from = stops[0];
  let to = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i += 1) {
    if (t >= stops[i].stop && t <= stops[i + 1].stop) {
      from = stops[i];
      to = stops[i + 1];
      break;
    }
  }

  const localT = (t - from.stop) / (to.stop - from.stop || 1);
  const r = Math.round(from.color[0] + (to.color[0] - from.color[0]) * localT);
  const g = Math.round(from.color[1] + (to.color[1] - from.color[1]) * localT);
  const b = Math.round(from.color[2] + (to.color[2] - from.color[2]) * localT);

  return `rgb(${r}, ${g}, ${b})`;
};

const getBarGradient = (ratio) => {
  const base = getBarColorFromRatio(ratio);
  const highlight = getBarColorFromRatio(Math.max(0, ratio - 0.12));
  return `linear-gradient(90deg, ${highlight} 0%, ${base} 100%)`;
};

const TrendAnalysis = ({ vehicleId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    setIsLoading(true);

    const fetchData = async () => {
      try {
        const url = vehicleId
          ? `${API_BASE_URL}/api/violation-percentage-with-descriptions?vehicleId=${vehicleId}`
          : `${API_BASE_URL}/api/violation-percentage-with-descriptions`;

        const response = await fetch(url, { signal });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setTableData(data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error:', error);
          setTableData([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [vehicleId]);

  const header = (
    <div className="trend-analysis-header">
      <h3>
        Trend Analysis / Challenges
        <span className="info-tooltip">
          <span className="info-icon">i</span>
          <span className="tooltip-text">{tooltipTexts.trendAnalysis}</span>
        </span>
      </h3>
    </div>
  );

  if (isLoading) {
    return (
      <div className="trend-analysis-card chart-container">
        {header}
        <div className="loading-container">
          <div className="trend-analysis-loader" aria-hidden="true" />
          Loading trend data...
        </div>
      </div>
    );
  }

  if (!tableData.length) {
    return (
      <div className="trend-analysis-card chart-container">
        {header}
        <div className="loading-container">
          No data available
        </div>
      </div>
    );
  }

  const maxPercentage = Math.max(
    ...tableData.map((item) => parseFloat(item.ViolationPercentage) || 0),
    1
  );

  const sortedData = [...tableData].sort(
    (a, b) => parseFloat(b.ViolationPercentage) - parseFloat(a.ViolationPercentage)
  );

  return (
    <div className="trend-analysis-card chart-container">
      {header}
      <div className="trend-table-container">
        <table className="trend-table">
          <thead>
            <tr>
              <th>Violations</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => {
              const percentage = parseFloat(item.ViolationPercentage) || 0;
              const ratio = percentage / maxPercentage;
              const barWidth = Math.max(ratio * 100, 8);
              const barColor = getBarGradient(ratio);
              const description =
                item.ViolationDescription ||
                violationDescriptions[item.ViolationScreenName] ||
                '';
              const labelClass =
                percentage < 12 ? 'bar-label outside-label' : 'bar-label';

              return (
                <tr key={`${item.ViolationScreenName}-${index}`}>
                  <td>
                    <div className="violation-name-container">
                      <span
                        className="violation-tooltip"
                        data-tooltip={description || undefined}
                        title={!description ? item.ViolationScreenName : undefined}
                      >
                        {item.ViolationScreenName}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="bar-container">
                      <div
                        className="percentage-bar"
                        style={{
                          width: `${barWidth}%`,
                          background: barColor
                        }}
                      >
                        {percentage > 0 && (
                          <span className={labelClass}>
                            {percentage.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

TrendAnalysis.propTypes = {
  vehicleId: PropTypes.string
};

export default TrendAnalysis;
